import { Box, Button, Checkbox, CheckboxGroup, Flex, Text, VStack } from '@chakra-ui/react';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import merge from 'lodash.merge';
import { RootState, store, useAppSelector } from 'modules/Editor/store';
import { selectWidget, selectWidgets } from 'modules/Editor/store/infographSelector';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import { generateDefaultData as generateDefaultMultipleChoiceData } from 'widgets/ResponsiveWidgets/MultipleChoicesWidget/MultipleChoicesWidget.helpers';
import { generateDefaultData as generateTextData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.helpers';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';
import { getTextWidgetProperties } from 'widgets/TextBasedWidgets/TextWidget/TextWidgetData.helpers';
import { useWidget } from 'widgets/sdk';

import { ANSWERS_LIMIT } from '../../MultipleChoicesWidget.config';
import {
  ComponentWidgetIdKeys,
  MultipleChoicesType,
  MultipleChoicesWidgetData,
} from '../../MultipleChoicesWidget.types';

/**
 * Render the question text on the side panel settings
 */
const QuestionText = () => {
  const { componentWidgetIdMap } = useWidget<MultipleChoicesWidgetData>();

  const questionWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.QuestionText];
  const questionText =
    getTextWidgetProperties((useAppSelector(selectWidget(questionWidgetId)) as TextWidgetData)?.proseMirrorData)
      ?.text || '';

  return (
    <Box w='full'>
      <Text lineHeight={6} fontSize={'sm'} color={'font.500'}>
        {questionText}
      </Text>
    </Box>
  );
};

/**
 * Render the correct answers config on the side panel settings
 */
const CorrectSection = () => {
  const { t } = useTranslation('editor_widget_toolbar', {
    keyPrefix: 'multipleChoicesToolbar',
    useSuspense: false,
  });
  const widgets = useAppSelector(selectWidgets);
  const { widgetId, componentWidgetIdMap, correctAnswers, replaceWidget, updateWidget, memberWidgetIds } =
    useWidget<MultipleChoicesWidgetData>();

  const questionWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.QuestionText];
  const answerWidgetIdList = memberWidgetIds.filter((id) => id !== questionWidgetId);
  const answerWidgetTextList = answerWidgetIdList
    .map((id) => widgets?.[id])
    .map((widget) => getTextWidgetProperties((widget as TextWidgetData)?.proseMirrorData)?.text || '');

  const disableAddButton = answerWidgetIdList.length >= ANSWERS_LIMIT;

  const handleCheckboxChange = (value: string[]) => {
    updateWidget({
      correctAnswers: value,
    });
  };

  // TODO: [JB] This will be moved to helper function in the next slice
  /**
   * Merge the data of the current widget with the new data
   * And overwrite data for existing group member widgets to maintained it
   */
  const mergeData = useCallback(
    (
      operator: 'add' | 'delete',
      targetType: MultipleChoicesType,
      originWidgetData: MultipleChoicesWidgetData,
      memberWidgetIds: string[] = [],
    ) => {
      const existingWidgetData = originWidgetData;
      const targetWidgetDefaultData = generateDefaultMultipleChoiceData(targetType);
      targetWidgetDefaultData.widgetData = merge(targetWidgetDefaultData.widgetData, existingWidgetData);

      // Set the exisiting questionWdiget
      const originQuestionTextWidgetData = (store.getState() as RootState).infograph.widgets[
        questionWidgetId
      ] as TextWidgetData;
      targetWidgetDefaultData.groupWidgets = [
        {
          widgetType: WidgetType.Text,
          widgetData: originQuestionTextWidgetData as any,
          componentKey: ComponentWidgetIdKeys.QuestionText,
        },
      ];

      // Set the exisiting memberWidgets
      memberWidgetIds.forEach((targetWidgetId, index) => {
        // Ignore question text
        if (index === 0) return;

        const originTextWidgetData = (store.getState() as RootState).infograph.widgets[
          targetWidgetId
        ] as TextWidgetData;
        if (originTextWidgetData) {
          targetWidgetDefaultData.groupWidgets.push({
            widgetType: WidgetType.Text,
            widgetData: originTextWidgetData as any,
            componentKey: ComponentWidgetIdKeys.AnswerText,
          });
        } else {
          targetWidgetDefaultData.groupWidgets.push({
            ...generateTextData(),
            componentKey: ComponentWidgetIdKeys.AnswerText,
          });
        }
      });

      if (operator === 'add') {
        // Add the one more widget
        targetWidgetDefaultData.groupWidgets.push({
          ...generateTextData(),
          componentKey: ComponentWidgetIdKeys.AnswerText,
        });
      }

      return targetWidgetDefaultData;
    },
    [questionWidgetId],
  );

  const handleDeleteChoice = useCallback(
    (targetWidgetId: WidgetId) => {
      const currentWidgetData = (store.getState() as RootState).infograph.widgets[
        widgetId
      ] as MultipleChoicesWidgetData;

      const newMemberWidgetIds = currentWidgetData.memberWidgetIds.filter((id) => id !== targetWidgetId);
      replaceWidget(mergeData('delete', MultipleChoicesType.Horizontal, currentWidgetData, newMemberWidgetIds));
    },
    [widgetId, mergeData, replaceWidget],
  );

  const handleAddChoice = useCallback(() => {
    const currentWidgetData = (store.getState() as RootState).infograph.widgets[widgetId] as MultipleChoicesWidgetData;
    replaceWidget(
      mergeData('add', MultipleChoicesType.Horizontal, currentWidgetData, currentWidgetData.memberWidgetIds),
    );
  }, [widgetId, mergeData, replaceWidget]);

  return (
    <Box w='full'>
      <CheckboxGroup colorScheme='green' defaultValue={[]} value={correctAnswers} onChange={handleCheckboxChange}>
        <Flex gap='2' flexDir={'column'}>
          {answerWidgetIdList.map((widgetId, index) => (
            <Flex key={widgetId} justifyContent={'flex-start'} w={'full'} p={0} m={0}>
              <Checkbox value={index.toString()} w='full'>
                <Text lineHeight={'6'} fontSize={'sm'} color={'font.500'} w={'full'}>
                  {answerWidgetTextList[index]}
                </Text>
              </Checkbox>
              <Button
                variant='unstyled'
                minW='20px'
                width='20px'
                height='20px'
                onClick={() => handleDeleteChoice(widgetId)}
              >
                <DeleteIcon width='20px' height='20px' color='var(--vg-colors-font-500)' />
              </Button>
            </Flex>
          ))}
        </Flex>
      </CheckboxGroup>
      <Button variant='action' isDisabled={disableAddButton} borderRadius={1} w='full' mt={4} onClick={handleAddChoice}>
        {t('settingsSideBar.multipleChoices.addButton')}
      </Button>
    </Box>
  );
};

/**
 * Render the question section on the side panel settings
 */
export const QuestionSection = memo(() => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'multipleChoicesToolbar', useSuspense: false });

  return (
    <VStack>
      <QuestionText />
      <Box w={'full'} textAlign={'left'}>
        <Text fontSize={'sm'} fontWeight='bold' my={2} w={'full'} textAlign={'left'}>
          {t('settingsSideBar.multipleChoices.correct')}
        </Text>
      </Box>
      <CorrectSection />
    </VStack>
  );
});

QuestionSection.displayName = 'Question Section';

export default QuestionSection;
